import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../supabaseClient'
import { useAuth } from '../contexts/AuthContext'
import { useI18n } from '../i18n'
import { logActivity } from '../lib/activity'
import { makeTitle, writeGift } from '../lib/gifts'
import { suggestIcon } from '../lib/giftIcons'
import { readableText, generateHexColor, fetchTakenColors } from '../lib/color'
import GiftFormModal from '../components/GiftFormModal'

const applyColor = (hex) => {
  const r = parseInt(hex.slice(1, 3), 16)
  const g = parseInt(hex.slice(3, 5), 16)
  const b = parseInt(hex.slice(5, 7), 16)
  document.documentElement.style.setProperty('--user-color', hex)
  document.documentElement.style.setProperty('--user-color-text', readableText(hex))
  document.documentElement.style.setProperty('--user-color-soft', `rgba(${r},${g},${b},0.10)`)
  document.documentElement.style.setProperty('--user-color-glow', `rgba(${r},${g},${b},0.25)`)
}

const FadeScreen = ({ children }) => (
  <div className="fade-in" style={{
    minHeight: '100vh', background: 'var(--background)',
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    justifyContent: 'center', padding: '32px 16px',
  }}>
    {children}
  </div>
)

const PrimaryBtn = ({ onClick, disabled, children }) => (
  <button className="btn-primary" onClick={onClick} disabled={disabled}
    style={{ marginTop: 32, minWidth: 160 }}>
    {children}
  </button>
)

const OnboardingFlow = () => {
  const navigate = useNavigate()
  const { user, profile, refreshProfile } = useAuth()
  const { t, lang } = useI18n()
  const [screen, setScreen] = useState(0)
  const [hexColor, setHexColor] = useState('')
  const [answers, setAnswers] = useState({ q4: '', q4b: '' })
  const [loading, setLoading] = useState(false)
  const [cardVisible, setCardVisible] = useState(false)
  const [saveError, setSaveError] = useState('')
  const [createdGift, setCreatedGift] = useState(null)
  const [editingGift, setEditingGift] = useState(false)

  useEffect(() => {
    const existing = profile?.hex_code
    if (existing) {
      setHexColor(existing)
      applyColor(existing)
    } else {
      fetchTakenColors().catch(() => []).then((taken) => {
        const hex = generateHexColor(taken)
        setHexColor(hex)
        applyColor(hex)
      })
    }
  }, [profile])

  const goNext = () => setScreen((s) => s + 1)

  const tryAnotherColor = async () => {
    const hex = generateHexColor(await fetchTakenColors().catch(() => []))
    setHexColor(hex)
    applyColor(hex)
  }

  const saveHexAndProceed = async () => {
    if (!profile?.hex_code) {
      let hex = hexColor
      let attempts = 0
      while (attempts < 6) {
        const { error } = await supabase
          .from('profiles')
          .update({ hex_code: hex })
          .eq('id', user.id)
        if (!error) {
          if (hex !== hexColor) {
            setHexColor(hex)
            applyColor(hex)
          }
          break
        }
        // 23505 = Postgres unique_violation — retry with a fresh colour
        if (error.code === '23505') {
          hex = generateHexColor(await fetchTakenColors().catch(() => []))
          attempts++
          continue
        }
        console.error('saveHexAndProceed error:', error)
        break
      }
    }
    setScreen(6)
  }

  const saveAnswer = async (questionIndex, text) => {
    await supabase.from('onboarding_answers').upsert(
      { user_id: user.id, question_index: questionIndex, answer_text: text },
      { onConflict: 'user_id,question_index' }
    )
  }

  const handleQ4 = async () => {
    if (!answers.q4.trim()) return
    setLoading(true)
    setSaveError('')
    try {
      await saveAnswer(4, answers.q4)
      if (answers.q4b.trim()) await saveAnswer(5, answers.q4b)

      const title = makeTitle(answers.q4)
      const { data: giftRow, error: giftErr } = await writeGift((row) => supabase.from('gifts').insert(row).select().single(), {
        creator_id: user.id,
        title,
        description: answers.q4,
        why_me: answers.q4b || null,
        visibility: 'community',
        icon: suggestIcon(answers.q4),
        status: 'active',
        is_active: true,
        creator_hex: hexColor,
        lang: lang || 'en',
      })
      if (giftErr) {
        console.error('onboarding gift insert error:', giftErr)
        setSaveError(`${t.onboarding.saveError} (${giftErr.message})`)
        return
      }
      setCreatedGift(giftRow)

      await supabase.from('profiles').update({ onboarding_completed: true }).eq('id', user.id)
      logActivity(user.id, 'onboarding_complete')
      await refreshProfile()
      goNext()
      setTimeout(() => setCardVisible(true), 400)
    } catch (err) {
      console.error('handleQ4 error:', err)
    } finally {
      setLoading(false)
    }
  }

  // Come in without a card for now; the dashboard keeps offering "add your first gift".
  const handleSkip = async () => {
    setLoading(true)
    try {
      await supabase.from('profiles').update({ onboarding_completed: true }).eq('id', user.id)
      logActivity(user.id, 'onboarding_skip')
      await refreshProfile()
      navigate('/dashboard')
    } catch (err) {
      console.error('handleSkip error:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleSaveGift = async (fields) => {
    const patch = {
      title: fields.title,
      description: fields.description,
      why_me: fields.why_me,
      gift_type: fields.gift_type,
      quota: fields.quota,
      is_physical: fields.is_physical,
      delivery_note: fields.delivery_note,
      visibility: fields.visibility,
      icon: fields.icon,
    }
    if (fields.gift_type === 'quota') patch.quota_remaining = fields.quota
    const { data, error } = await writeGift((row) => supabase.from('gifts').update(row).eq('id', createdGift.id).select().single(), patch)
    if (error) throw error
    setCreatedGift(data)
    setEditingGift(false)
  }

  const mono = { fontFamily: "'DM Mono', monospace", fontSize: 15, color: 'var(--text-muted)', textAlign: 'center' }
  const heading = { fontFamily: "'DM Sans', sans-serif", fontWeight: 500, color: 'var(--text-primary)', textAlign: 'center' }
  const explanation = { fontFamily: "'DM Sans', sans-serif", fontSize: 17, color: 'var(--text-muted)', lineHeight: 1.85, maxWidth: 560, width: '100%', marginBottom: 28, whiteSpace: 'pre-line' }

  // Shared style for question headings — smaller on mobile via clamp
  const questionHeading = {
    ...heading,
    fontSize: 'clamp(17px, 3.5vw, 22px)',
    maxWidth: 560,
    lineHeight: 1.6,
    marginTop: 24,
    marginBottom: 32,
    wordBreak: 'break-word',
    overflowWrap: 'break-word',
  }

  if (screen === 0) return (
    <FadeScreen>
      <div style={{ maxWidth: 520, width: '100%' }}>
        <p style={{ ...heading, fontSize: 'clamp(26px, 5vw, 36px)', lineHeight: 1.3, marginBottom: 32, textAlign: 'left' }}>
          {t.onboarding.screenVillage.line1}
        </p>
        {[t.onboarding.screenVillage.line2, t.onboarding.screenVillage.line3, t.onboarding.screenVillage.line4, t.onboarding.screenVillage.line5].map((line, i) => (
          <p key={i} style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 17, color: 'var(--text-muted)', lineHeight: 1.85, marginBottom: 16 }}>
            {line}
          </p>
        ))}
      </div>
      <div style={{ maxWidth: 520, width: '100%', marginTop: 16 }}>
        <p style={{ margin: '0 0 20px' }}><a href="/manifesto" target="_blank" rel="noopener noreferrer" style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 15, color: 'var(--text-muted)', textDecoration: 'underline' }}>{t.about.what}</a></p>
        <PrimaryBtn onClick={() => setScreen(1)}>{t.onboarding.screenVillage.cta}</PrimaryBtn>
      </div>
    </FadeScreen>
  )

  if (screen === 1) return (
    <FadeScreen>
      <div style={{
        width: 120, height: 120, borderRadius: '50%', background: hexColor,
        boxShadow: `0 0 48px var(--user-color-glow)`, marginBottom: 24,
      }} />
      <p style={{ ...mono, fontSize: 20, marginBottom: 16 }}>{hexColor}</p>
      <p style={{ ...heading, fontSize: 20, maxWidth: 400, lineHeight: 1.7, color: 'var(--text-muted)' }}>
        {t.onboarding.screen1.headline}
      </p>
      <button onClick={tryAnotherColor} style={{ ...heading, background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, color: 'var(--text-muted)', textDecoration: 'underline', margin: '4px 0 8px' }}>
        {t.onboarding.screen1.another}
      </button>
      <PrimaryBtn onClick={saveHexAndProceed}>{t.onboarding.screen1.cta}</PrimaryBtn>
    </FadeScreen>
  )

  if (screen === 6) return (
    <FadeScreen>
      <h2 style={{ ...questionHeading, marginTop: 0, marginBottom: 12 }}>{t.onboarding.giftQuestion}</h2>
      <p style={{ ...explanation, textAlign: 'center', marginBottom: 24 }}>{t.onboarding.giftIntro}</p>
      <textarea
        style={{ maxWidth: 560, width: '100%' }}
        value={answers.q4}
        onChange={(e) => setAnswers({ ...answers, q4: e.target.value })}
        placeholder={t.give.offerPlaceholder}
        autoFocus
      />
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, maxWidth: 560, width: '100%', marginTop: 16 }}>
        {t.give.examples.map((ex) => (
          <button
            key={ex.label}
            type="button"
            onClick={() => setAnswers({ ...answers, q4: ex.text })}
            style={{
              padding: '7px 14px', borderRadius: 999, cursor: 'pointer', fontSize: 14,
              fontFamily: "'DM Sans', sans-serif", color: 'var(--text-primary)',
              background: 'var(--surface-2)', border: '1px solid var(--border)',
            }}
          >
            {ex.label}
          </button>
        ))}
      </div>

      {saveError && (
        <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 15, color: '#e05c5c', maxWidth: 560, marginTop: 16 }}>{saveError}</p>
      )}

      <PrimaryBtn onClick={handleQ4} disabled={loading || !answers.q4.trim()}>
        {loading ? t.onboarding.saving : t.onboarding.screen6.cta}
      </PrimaryBtn>
      <button
        type="button"
        onClick={handleSkip}
        disabled={loading}
        style={{ marginTop: 16, background: 'none', border: 'none', cursor: 'pointer', fontFamily: "'DM Sans', sans-serif", fontSize: 15, color: 'var(--text-muted)', textDecoration: 'underline' }}
      >
        {t.onboarding.skipGift}
      </button>
    </FadeScreen>
  )

  if (screen === 7) {
    const card = createdGift || { title: makeTitle(answers.q4), description: answers.q4 }
    const showDesc = card.description && card.description.trim() !== (card.title || '').trim()
    return (
      <FadeScreen>
        {cardVisible && (
          <div className="fade-in" style={{ maxWidth: 400, width: '100%' }}>
            <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 17, color: 'var(--text-muted)', textAlign: 'center', margin: '0 0 20px' }}>
              {t.onboarding.screen7.ready}
            </p>

            <div className="armagan-card" style={{
              background: `rgba(${parseInt(hexColor.slice(1,3),16)},${parseInt(hexColor.slice(3,5),16)},${parseInt(hexColor.slice(5,7),16)},0.12)`,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                <div style={{ width: 24, height: 24, borderRadius: '50%', background: hexColor }} />
                <span className="mono">{hexColor}</span>
              </div>
              <h3 style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 20, fontWeight: 500, margin: showDesc ? '0 0 8px' : 0 }}>
                {card.title}
              </h3>
              {showDesc && (
                <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 16, color: 'var(--text-muted)', margin: 0 }}>
                  {card.description}
                </p>
              )}
            </div>

            <div style={{ display: 'flex', gap: 12, justifyContent: 'center', marginTop: 28 }}>
              {createdGift && (
                <button className="btn-secondary" onClick={() => setEditingGift(true)} style={{ minWidth: 120 }}>
                  {t.onboarding.screen7.edit}
                </button>
              )}
              <button className="btn-primary" onClick={() => navigate('/dashboard')} style={{ minWidth: 160 }}>
                {t.onboarding.screen7.cta}
              </button>
            </div>
          </div>
        )}

        {editingGift && createdGift && (
          <GiftFormModal
            card={createdGift}
            profile={profile}
            t={t}
            onClose={() => setEditingGift(false)}
            onCreate={handleSaveGift}
          />
        )}
      </FadeScreen>
    )
  }

  return null
}

export default OnboardingFlow
